<script lang="ts">
  /**
   * ClubDayRoster — the Club tab's landing under the strip (build 15, spec 2026-09-14).
   *
   *   SWAM WED · 3 SWIMS
   *   one row per swim (lib/club/roster.ts orders them): name (+ time of day on a swimmer's
   *   2+ swims), distance · duration · pool, an `edited` mark when a saved label changed a
   *   stroke, the swim's lengths as ONE capped SetGroupRibbon line with its stroke caption.
   *   Tap the row: that swim's card (the tab swaps it in). Own rows: `Review strokes ›`, the
   *   correction entry point (ownSetTap to the Swim tab by UUID). Every row: the six preset
   *   emoji with counts, no tap needed to see them; pending and failure show on that row only.
   *   A single-swimmer day adds "Ryan last swam Tue 8 ›" lines.
   *
   * Nothing here opens by itself, ranks, or compares: numbers side by side, no deltas.
   */
  import type { ClubReaction, PresetEmoji } from "../lib/club/snapshot";
  import { PRESET_EMOJI } from "../lib/club/snapshot";
  import { reactionsForSwim, receivedCounts, receivedLabel } from "../lib/club/strip";
  import { NOT_ON_PHONE_LINE, noLengthsLine, rowSetGroup, shareKind, shareLine, sourceKind, sourceLine } from "../lib/club/swimCard";
  import type { LastSwamLine, RosterRow } from "../lib/club/roster";
  import type { ReactStatus } from "../lib/club/reactState";
  import { rowMaxChips } from "../lib/setPage";
  import { haptic } from "../lib/haptics";
  import SetGroupRibbon from "./SetGroupRibbon.svelte";

  interface Props {
    eyebrow: string;
    rows: RosterRow[];
    lines: LastSwamLine[];
    /** "No swims yet: Ava, Ben": the members a single-swimmer day would otherwise leave out. */
    noSwims: string | null;
    selfId: string;
    /** Fresh (24 h) reactions, every swim. */
    reactions: readonly ClubReaction[];
    reactStatus: Record<string, ReactStatus>;
    onOpen: (row: RosterRow) => void;
    onReviewStrokes: (row: RosterRow) => void;
    onReact: (swimId: string, emoji: PresetEmoji | null) => void;
    onGoToDay: (dayKey: string) => void;
  }
  let { eyebrow, rows, lines, noSwims, selfId, reactions, reactStatus, onOpen, onReviewStrokes, onReact, onGoToDay }: Props = $props();

  function tap(row: RosterRow, emoji: PresetEmoji, mine: PresetEmoji | null): void {
    haptic("light");
    // Tapping your own reaction again removes it (C5); any other emoji replaces it.
    onReact(row.swim.id, mine === emoji ? null : emoji);
  }

  function open(row: RosterRow): void {
    haptic("selection");
    onOpen(row);
  }

  function openLabel(row: RosterRow): string {
    const tod = row.timeOfDay ? ` at ${row.timeOfDay}` : "";
    return `${row.name}${tod}, ${row.line}${row.sets.edited ? ", edited" : ""}. Opens ${row.isSelf ? "your" : `${row.name}'s`} swim.`;
  }
</script>

<!-- A div, not a section: app.css frames every `#app-root section[class]` as a glass card. -->
<div class="roster" data-testid="club-roster">
  <p class="eyebrow" data-testid="club-roster-eyebrow">{eyebrow}</p>

  {#each rows as row (row.swim.id)}
    {@const line = sourceLine(row.sets)}
    <div class="row" data-testid="club-roster-row" data-swim-id={row.swim.id} data-member-id={row.member.id}>
      <button type="button" class="open" data-testid="club-roster-open" aria-label={openLabel(row)} onclick={() => open(row)}>
        <span class="main">
          <span class="who">
            <span class="name">{row.name}</span>
            {#if row.timeOfDay}<span class="tod" data-testid="club-roster-tod">{row.timeOfDay}</span>{/if}
            {#if row.sets.edited}<span class="edited" data-testid="club-roster-edited">edited</span>{/if}
          </span>
          <span class="nums">{row.line}</span>
          {#if row.laps.length > 0}
            <span class="ribbon" class:estimate={row.sets.source === "summary"}><SetGroupRibbon setGroup={rowSetGroup(row.laps)} maxChips={rowMaxChips(row.laps.length)} /></span>
            <span class="caption" data-testid="club-roster-caption">{row.caption}</span>
            {#if line}<span class="status {sourceKind(row.sets)}" data-testid="club-roster-source">{line}</span>{/if}
          {:else if row.sets.source === "none"}
            <span class="caption" data-testid="club-roster-caption">{NOT_ON_PHONE_LINE}</span>
          {:else if line}
            <span class="status {sourceKind(row.sets)}" data-testid="club-roster-source">{line}</span>
          {:else}
            <span class="caption" data-testid="club-roster-caption">{noLengthsLine(row.swim)}</span>
          {/if}
          {#if row.isSelf && shareLine(row.sets)}<span class="status {shareKind(row.sets)}" data-testid="club-roster-share">{shareLine(row.sets)}</span>{/if}
        </span>
        <span class="chev" aria-hidden="true">›</span>
      </button>

      {#if row.isSelf}
        <button type="button" class="review" data-testid="club-review-strokes" onclick={() => onReviewStrokes(row)}>Review strokes ›</button>
      {/if}

      {#if row.reactable && row.isSelf}
        <!-- Build 15b: no picker on your own swim, only what the club gave it (nothing when none did). -->
        {@const received = receivedCounts(reactions, row.swim.id, selfId)}
        {#if received.length > 0}
          <p class="received" data-testid="club-react-counts" data-swim-id={row.swim.id} aria-label={receivedLabel(received)}>
            {#each received as c (c.emoji)}<span class="count">{c.emoji}<span class="n">{c.count}</span></span>{/each}
          </p>
        {/if}
      {:else if row.reactable}
        {@const summary = reactionsForSwim(reactions, row.swim.id, selfId)}
        {@const st = reactStatus[row.swim.id]}
        <div
          class="react"
          class:pending={st?.pending}
          data-testid="club-react-picker"
          data-swim-id={row.swim.id}
          role="group"
          aria-busy={st?.pending ? "true" : undefined}
          aria-label={`React to ${row.name}'s swim${row.timeOfDay ? ` at ${row.timeOfDay}` : ""}`}
        >
          {#each PRESET_EMOJI as emoji (emoji)}
            {@const n = summary.counts.find((c) => c.emoji === emoji)?.count ?? 0}
            <button
              type="button"
              class="emoji"
              class:mine={summary.mine === emoji}
              data-emoji={emoji}
              aria-pressed={summary.mine === emoji}
              aria-label={`${emoji}${n > 0 ? `, ${n}` : ""}${summary.mine === emoji ? ", yours, tap to remove" : ""}`}
              onclick={() => tap(row, emoji, summary.mine)}
            >
              <span class="glyph">{emoji}</span>
              {#if n > 0}<span class="n">{n}</span>{/if}
            </button>
          {/each}
        </div>
        {#if st?.pending}
          <p class="react-status" data-testid="club-react-pending" role="status">Sending…</p>
        {:else if st?.failed}
          <p class="react-fail" data-testid="club-react-fail" role="status">Couldn't send that to the club. Check your connection and tap again.</p>
        {/if}
      {/if}
    </div>
  {/each}

  {#each lines as line (line.member.id)}
    {#if line.linked}
      <button type="button" class="last" data-testid="club-last-swam" data-day={line.dayKey} onclick={() => onGoToDay(line.dayKey)}>{line.text} ›</button>
    {:else}
      <p class="last plain" data-testid="club-last-swam" data-day={line.dayKey}>{line.text}</p>
    {/if}
  {/each}
  {#if noSwims}
    <p class="last plain" data-testid="club-no-swims">{noSwims}</p>
  {/if}
</div>

<style>
  .roster {
    display: flex;
    flex-direction: column;
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: 12px var(--space-4) var(--space-3);
    min-width: 0;
  }
  .eyebrow {
    font: var(--type-caption);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin: 0 0 4px;
  }
  .row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 0 12px;
    min-width: 0;
  }
  .row + .row {
    border-top: 1px solid var(--divider);
  }
  .open {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-width: 0;
    min-height: 56px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    text-align: left;
    font-family: var(--font-text);
    color: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .open:active {
    background: var(--bg-raised);
  }
  .main {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }
  .who {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .name {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tod {
    font-size: 14px;
    color: var(--text-muted);
    white-space: nowrap;
  }
  .edited {
    font: var(--type-caption);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-body);
    border: 1px solid var(--divider);
    border-radius: var(--radius-pill);
    padding: 1px 7px;
    white-space: nowrap;
  }
  .nums {
    font-size: 14px;
    color: var(--text-body);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .caption {
    font-size: 12px;
    letter-spacing: 0.02em;
    color: var(--text-muted);
  }
  /* Build 16: where the sets came from when not derived, and an own correction not yet shared. */
  /* VP round 1: one cue per meaning. A glyph before the words (hidden from VoiceOver, the words carry
     it: the empty alt text after the slash keeps the glyph out of VoiceOver) and a color: estimate in its own accent and weight with the ribbon dimmed (VP round 2), pending body, error the error color. */
  .ribbon {
    display: block;
    min-width: 0;
  }
  .ribbon.estimate {
    opacity: 0.55;
  }
  .status {
    font-size: 13px;
    color: var(--text-body);
  }
  .status::before {
    display: inline-block;
    width: 1.1em;
    font-weight: 700;
  }
  .status.estimate {
    color: var(--accent-secondary);
    font-weight: 600;
  }
  .status.estimate::before {
    content: "≈" / "";
  }
  .status.pending::before {
    content: "↻" / "";
  }
  .status.error {
    color: var(--accent-error);
  }
  .status.error::before {
    content: "!" / "";
  }
  .chev {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 1;
    color: var(--text-dim);
  }
  .review {
    align-self: flex-start;
    min-height: 44px;
    padding: 0 var(--space-3);
    margin: -4px 0 -2px calc(-1 * var(--space-3));
    background: transparent;
    border: none;
    font-family: var(--font-text);
    font-size: 15px;
    font-weight: 600;
    color: var(--accent-primary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  /* Six emoji, one row, 44 px tap targets (the card's picker grammar). */
  .react {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 6px;
  }
  .react.pending .emoji {
    opacity: 0.6;
  }
  /* Your own swim: counts as plain pills, no border, so nothing reads as a button (build 15b). */
  .received {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
  }
  .count {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 28px;
    padding: 0 10px;
    border-radius: var(--radius-pill);
    background: var(--bg-raised);
    font-size: 16px;
    line-height: 1;
  }
  .emoji {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 44px;
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    font-family: var(--font-text);
    cursor: pointer;
    padding: 0 4px;
    color: var(--text-primary);
  }
  .emoji.mine {
    border-color: var(--accent-primary);
    background: var(--bg-highest);
  }
  .glyph {
    font-size: 20px;
    line-height: 1;
  }
  .n {
    font: var(--type-caption);
    color: var(--text-muted);
  }
  .emoji.mine .n {
    color: var(--text-primary);
  }
  .react-status,
  .react-fail {
    font-size: 13px;
    margin: 0;
  }
  .react-status {
    color: var(--text-muted);
  }
  .react-fail {
    color: var(--accent-error);
  }
  .last {
    display: block;
    width: 100%;
    text-align: left;
    font-family: var(--font-text);
    font-size: 15px;
    padding: 12px 0;
    margin: 0;
    border: none;
    border-top: 1px solid var(--divider);
    background: transparent;
    color: var(--accent-primary);
    min-height: 44px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  /* Plain lines read as text, not as a quieter link: smaller, dim, no chevron (VP round 1). */
  .last.plain {
    font-size: 14px;
    color: var(--text-muted);
    min-height: 0;
    padding: 10px 0;
    cursor: default;
  }
</style>
